import { api } from './index'
import type { SsoStart, StepUpWindow } from './auth'
import { ensureOk, unwrap } from './client'
import type { components } from './schema'

export type SsoProvider = components['schemas']['SsoProvider']
export type SsoSettings = components['schemas']['SsoSettings']
export type SsoSettingsWrite = components['schemas']['SsoSettingsWrite']
/** The redirect URI to register with the provider, and the settings if any (MRS 3.1, D42). */
export type SsoState = components['schemas']['SsoState']

export async function fetchSsoSettings(): Promise<SsoState> {
  return unwrap<SsoState>(await api.GET('/api/v1/sso'))
}

/** Behind step-up. Creates or replaces; the secret is write-only and may be left out on an update. */
export async function saveSsoSettings(body: SsoSettingsWrite): Promise<SsoState> {
  return unwrap<SsoState>(await api.PUT('/api/v1/sso', { body }))
}

/** Behind step-up. People sign in with their password again; their accounts stay. */
export async function removeSsoSettings(): Promise<void> {
  ensureOk(await api.DELETE('/api/v1/sso'))
}

/**
 * Full session. Where to send the browser so the identity provider confirms
 * the person again (D91). 409 `sso_not_linked` when this session cannot step
 * up that way, 503 `idp_unavailable` when the provider cannot be reached.
 */
export async function startSsoStepUp(): Promise<SsoStart> {
  return unwrap<SsoStart>(await api.POST('/api/v1/auth/step-up/sso/start'))
}

/**
 * Full session. What the provider returned, exchanged for an open step-up
 * window. Any refusal is 422 on `credentials`, as the password path answers.
 */
export async function completeSsoStepUp(state: string, code: string): Promise<StepUpWindow> {
  return unwrap<StepUpWindow>(
    await api.POST('/api/v1/auth/step-up/sso/complete', { body: { state, code } }),
  )
}
